import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { PageTransition } from '@/components/PageTransition';
import { useSpaceData } from '@/hooks/useSpaceData';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { supabase } from '@/lib/supabase';
import {
  Play,
  Pencil,
  Eye,
  MoreHorizontal,
  Clock,
  CheckCircle2,
  Circle,
  Trash2,
  ListChecks,
  GitBranch,
  XCircle,
  Calendar,
  Webhook,
  Copy,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

// Parse template variables and replace with actual values
const parseRunNameTemplate = (template, context) => {
  if (!template) return '';

  const { user, workflow, runNumber } = context;

  const variables = {
    'current_user.name': `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Unknown User',
    'current_user.email': user?.email || '',
    'current_user.first_name': user?.first_name || '',
    'current_date.full': format(new Date(), 'MMMM d, yyyy'),
    'current_date.short': format(new Date(), 'MM/dd/yyyy'),
    'current_date.iso': format(new Date(), 'yyyy-MM-dd'),
    'current_time': format(new Date(), 'h:mm a'),
    'workflow.name': workflow?.name || 'Workflow',
    'run_number': String(runNumber || 1),
  };

  // Replace all {{ variable }} patterns
  return template.replace(/\{\{\s*([^}]+)\s*\}\}/g, (match, key) => {
    const trimmedKey = key.trim();
    return variables[trimmedKey] !== undefined ? variables[trimmedKey] : match;
  });
};

export default function WorkflowManagementPage() {
  const navigate = useNavigate();
  const { projectId, workflowId } = useParams();
  const { userProfile, company } = useAuth();
  // Pass company.id to validate the space belongs to the user's current company
  const { space, spaceTools, loading, accessDenied } = useSpaceData(projectId, userProfile?.id, company?.id);

  // Set page title
  usePageTitle('Workflow Management');

  // Redirect if user doesn't have access (e.g., switched companies)
  useEffect(() => {
    if (accessDenied) {
      toast.error('You no longer have access to this space');
      navigate('/dashboard', { replace: true });
    }
  }, [accessDenied, navigate]);

  const [workflow, setWorkflow] = useState(null);
  const [workflowRuns, setWorkflowRuns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showRunDialog, setShowRunDialog] = useState(false);
  const [runName, setRunName] = useState('');
  const [isCreatingRun, setIsCreatingRun] = useState(false);

  // Open run dialog with default name pre-filled
  const openRunDialog = () => {
    const template = workflow?.settings?.runName || '{{ current_user.name }} - {{ current_date.full }}';
    const defaultName = parseRunNameTemplate(template, {
      user: userProfile,
      workflow: workflow,
      runNumber: workflowRuns.length + 1
    });
    setRunName(defaultName);
    setShowRunDialog(true);
  };
  const [isTestingWebhook, setIsTestingWebhook] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [deleteRunId, setDeleteRunId] = useState(null);

  const SUPABASE_URL = 'https://yqmlqctpakqxtwzrdofl.supabase.co';
  const webhookUrl = workflow?.webhook_token
    ? `${SUPABASE_URL}/functions/v1/workflow-webhook?token=${workflow.webhook_token}`
    : null;
  const hasWebhookTrigger = workflow?.triggers?.some(t => t.id === 'webhook');

  const handleCopyWebhookUrl = async () => {
    if (!webhookUrl) return;
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(webhookUrl);
      } else {
        // Fallback for non-HTTPS or older browsers
        const textArea = document.createElement('textarea');
        textArea.value = webhookUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success('Webhook URL copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy URL');
    }
  };

  const handleTestWebhook = async () => {
    if (!webhookUrl) return;

    setIsTestingWebhook(true);
    try {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ run_name: `Test Run - ${new Date().toLocaleString()}` }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(`Webhook test successful! Run created: ${data.run_name}`);
        // Refresh runs list
        const { data: runsData } = await supabase
          .from('checklists_workflow_runs')
          .select(`
            *,
            started_by_user:user_profiles!checklists_workflow_runs_started_by_fkey(id, first_name, last_name, avatar_url),
            task_runs:checklists_workflow_task_runs(id, status, due_date)
          `)
          .eq('workflow_id', workflowId)
          .order('created_date', { ascending: false });
        setWorkflowRuns(runsData || []);
      } else {
        toast.error(data.error || 'Webhook test failed');
      }
    } catch (error) {
      console.error('Webhook test error:', error);
      toast.error('Failed to test webhook');
    } finally {
      setIsTestingWebhook(false);
    }
  };

  // Load workflow and runs
  useEffect(() => {
    const loadWorkflowData = async () => {
      if (!workflowId) return;

      try {
        // Fetch workflow
        const { data: workflowData, error: workflowError } = await supabase
          .from('workflows')
          .select('*')
          .eq('id', workflowId)
          .single();

        if (workflowError) throw workflowError;
        setWorkflow(workflowData);

        // Fetch workflow runs with task runs for due date
        const { data: runsData, error: runsError } = await supabase
          .from('checklists_workflow_runs')
          .select(`
            *,
            started_by_user:user_profiles!checklists_workflow_runs_started_by_fkey(id, first_name, last_name, avatar_url),
            task_runs:checklists_workflow_task_runs(id, status, due_date)
          `)
          .eq('workflow_id', workflowId)
          .order('created_date', { ascending: false });

        if (runsError) throw runsError;
        setWorkflowRuns(runsData || []);
      } catch (error) {
        console.error('Error loading workflow:', error);
        toast.error('Failed to load workflow');
      } finally {
        setIsLoading(false);
      }
    };

    loadWorkflowData();
  }, [workflowId]);

  // Real-time subscription for workflow runs
  useEffect(() => {
    if (!workflowId) return;

    let channel = null;
    const instanceId = Date.now();

    const setupSubscription = async () => {
      // Set auth token for Realtime (required for RLS policies)
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        await supabase.realtime.setAuth(session.access_token);
      }

      channel = supabase
        .channel(`workflow-runs-${workflowId}-${instanceId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'checklists_workflow_runs',
          filter: `workflow_id=eq.${workflowId}`,
        },
        async (payload) => {
          console.log('Workflow run change:', payload.eventType, payload);

          if (payload.eventType === 'INSERT') {
            // Fetch the full run data with relations
            const { data: newRun } = await supabase
              .from('checklists_workflow_runs')
              .select(`
                *,
                started_by_user:user_profiles!checklists_workflow_runs_started_by_fkey(id, first_name, last_name, avatar_url),
                task_runs:checklists_workflow_task_runs(id, status, due_date)
              `)
              .eq('id', payload.new.id)
              .single();

            if (newRun) {
              setWorkflowRuns((prev) => {
                // Deduplicate: skip if run already exists (e.g. from initial fetch or optimistic insert)
                if (prev.some((r) => r.id === newRun.id)) return prev;
                return [newRun, ...prev];
              });
              toast.success(`New run started: ${newRun.run_name}`);
            }
          } else if (payload.eventType === 'UPDATE') {
            // Update the run in the list
            const { data: updatedRun } = await supabase
              .from('checklists_workflow_runs')
              .select(`
                *,
                started_by_user:user_profiles!checklists_workflow_runs_started_by_fkey(id, first_name, last_name, avatar_url),
                task_runs:checklists_workflow_task_runs(id, status, due_date)
              `)
              .eq('id', payload.new.id)
              .single();

            if (updatedRun) {
              setWorkflowRuns((prev) =>
                prev.map((run) => (run.id === updatedRun.id ? updatedRun : run))
              );
            }
          } else if (payload.eventType === 'DELETE') {
            setWorkflowRuns((prev) =>
              prev.filter((run) => run.id !== payload.old.id)
            );
          }
        }
      )
      .subscribe((status, err) => {
        console.log('Workflow runs subscription status:', status);
        if (err) {
          console.error('Subscription error:', err);
        }
      });
    };

    setupSubscription();

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, [workflowId]);

  // Calculate stats
  const activeRuns = workflowRuns.filter(r => r.status === 'in_progress').length;
  const completedRuns = workflowRuns.filter(r => r.status === 'completed').length;
  const totalTasks = workflow?.tasks?.length || 0;

  // Filter runs based on toggle
  const filteredRuns = showCompleted
    ? workflowRuns
    : workflowRuns.filter(r => r.status !== 'completed');

  // Calculate due date based on task settings
  const calculateDueDate = (dueDateConfig) => {
    if (!dueDateConfig) return null;

    const now = new Date();
    let dueDate = new Date(now);

    const { months = 0, days = 0, hours = 0, minutes = 0, timingDirection } = dueDateConfig;

    // Calculate total offset in milliseconds
    let offsetMs = 0;
    offsetMs += months * 30 * 24 * 60 * 60 * 1000;
    offsetMs += days * 24 * 60 * 60 * 1000;
    offsetMs += hours * 60 * 60 * 1000;
    offsetMs += minutes * 60 * 1000;

    // Apply direction
    if (timingDirection === 'before') {
      dueDate = new Date(now.getTime() - offsetMs);
    } else {
      dueDate = new Date(now.getTime() + offsetMs);
    }

    return dueDate;
  };

  // Get the earliest due date from a run's task runs
  const getRunDueDate = (run) => {
    const taskRuns = run.task_runs || [];
    const dueDates = taskRuns
      .filter(t => t.due_date && t.status !== 'completed')
      .map(t => new Date(t.due_date));

    if (dueDates.length === 0) return null;
    return new Date(Math.min(...dueDates));
  };

  // Create a new workflow run
  const handleCreateRun = async () => {
    if (!workflow || !userProfile) return;

    setIsCreatingRun(true);
    try {
      // Get the run name template from workflow settings, or use default
      const runNameTemplate = workflow.settings?.runName || '{{ current_user.name }} - {{ current_date.full }}';

      // Parse the template with actual values
      const parsedRunName = runName || parseRunNameTemplate(runNameTemplate, {
        user: userProfile,
        workflow: workflow,
        runNumber: workflowRuns.length + 1,
      });

      // Create the workflow run
      const { data: runData, error: runError } = await supabase
        .from('checklists_workflow_runs')
        .insert({
          workflow_id: workflowId,
          project_id: projectId,
          run_name: parsedRunName,
          started_by: userProfile.id,
          status: 'in_progress',
        })
        .select()
        .single();

      if (runError) throw runError;

      // Create task runs only up to and including the first task with stopTask enabled
      // This allows the workflow to pause at stop points
      const tasks = workflow.tasks || [];
      const taskRunsToCreate = [];

      for (let i = 0; i < tasks.length; i++) {
        const task = tasks[i];

        // Calculate assignees based on task settings
        let assignees = [];
        if (task.assignees) {
          assignees = task.assignees.map(a => {
            if (a.type === 'workflow_runner') {
              return { type: 'user', id: userProfile.id, name: `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() };
            }
            return a;
          });
        }

        // Calculate due date if configured
        const dueDate = task.dueDate ? calculateDueDate(task.dueDate) : null;

        taskRunsToCreate.push({
          workflow_run_id: runData.id,
          task_id: String(task.id),
          task_order: i,
          status: i === 0 ? 'in_progress' : 'pending',
          assignees: assignees,
          due_date: dueDate?.toISOString() || null,
          form_data: {},
        });

        // If this task has stopTask enabled, don't create any more task runs
        if (task.stopTask) {
          break;
        }
      }

      if (taskRunsToCreate.length > 0) {
        const { error: taskRunsError } = await supabase
          .from('checklists_workflow_task_runs')
          .insert(taskRunsToCreate);

        if (taskRunsError) throw taskRunsError;
      }

      toast.success('Workflow run created!');
      setShowRunDialog(false);
      setRunName('');

      // Navigate to the run
      navigate(`/space/${projectId}/workflow-run/${runData.id}`);
    } catch (error) {
      console.error('Error creating workflow run:', error);
      toast.error('Failed to create workflow run');
    } finally {
      setIsCreatingRun(false);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'in_progress':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200"><Clock className="w-3 h-3 mr-1" /> In Progress</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" /> Completed</Badge>;
      case 'cancelled':
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200"><XCircle className="w-3 h-3 mr-1" /> Cancelled</Badge>;
      default:
        return <Badge variant="outline"><Circle className="w-3 h-3 mr-1" /> {status}</Badge>;
    }
  };

  // Get progress for a run
  const getRunProgress = (run) => {
    const taskRuns = run.task_runs || [];
    const completed = taskRuns.filter(t => t.status === 'completed').length;
    const total = taskRuns.length;
    return { completed, total, percent: total > 0 ? (completed / total) * 100 : 0 };
  };

  // Check if due date is overdue
  const isDueDateOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  if (loading || isLoading) {
    return (
      <AppShell3 projectId={projectId} activeTool="tasks">
        <PageTransition>
          <div className="min-h-screen flex items-center justify-center">
            <LoadingSpinner />
          </div>
        </PageTransition>
      </AppShell3>
    );
  }

  return (
    <AppShell3 projectId={projectId} activeTool="tasks">
      <ToolHeader
        spaceName={space?.name || 'Space'}
        spaceImage={space?.logo_url}
        toolName={workflow?.name || 'Workflow'}
        currentTool="tasks"
        spaceTools={spaceTools}
        parentBreadcrumb={{
          name: 'Library',
          onClick: () => navigate(`/space/${projectId}/tasks?view=library`)
        }}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/space/${projectId}/workflow-editor?id=${workflowId}`)}
            >
              <Pencil className="w-4 h-4 mr-1" />
              Edit
            </Button>
            <Button
              size="sm"
              onClick={openRunDialog}
            >
              <Play className="w-4 h-4 mr-1" />
              Run Workflow
            </Button>
          </div>
        }
      />
      <PageTransition>
        <div className="p-6 space-y-6">
          {/* Cover Section */}
          <div
            className="h-40 rounded-lg relative overflow-hidden"
            style={{
              background: workflow?.settings?.notificationCover?.type === 'image'
                ? `url(${workflow.settings.notificationCover.value}) center/cover`
                : workflow?.settings?.notificationCover?.value
                  || workflow?.settings?.background?.value
                  || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}
          >
            <div className="absolute inset-0 bg-black/20" />
            {/* Workflow Name Overlay */}
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <h1 className="text-white text-3xl font-bold drop-shadow-lg text-center">{workflow?.name}</h1>
            </div>
            {/* Description at bottom */}
            {workflow?.description && (
              <div className="absolute bottom-4 left-6 right-6">
                <p className="text-white/90 text-sm drop-shadow text-center">{workflow.description}</p>
              </div>
            )}
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-card border rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">{activeRuns}</p>
                  <p className="text-sm text-muted-foreground">Active Runs</p>
                </div>
              </div>
            </div>
            <div className="bg-card border rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">{completedRuns}</p>
                  <p className="text-sm text-muted-foreground">Completed</p>
                </div>
              </div>
            </div>
            <div className="bg-card border rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <ListChecks className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">{totalTasks}</p>
                  <p className="text-sm text-muted-foreground">Tasks in Workflow</p>
                </div>
              </div>
            </div>
            <div className="bg-card border rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <GitBranch className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">{workflowRuns.length}</p>
                  <p className="text-sm text-muted-foreground">Total Runs</p>
                </div>
              </div>
            </div>
          </div>

          {/* Webhook Section - Only show if webhook trigger is enabled */}
          {hasWebhookTrigger && webhookUrl && (
            <div className="bg-card border rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-violet-100 rounded-lg">
                  <Webhook className="w-4 h-4 text-violet-600" />
                </div>
                <h3 className="font-medium">Webhook Trigger</h3>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 ml-auto">
                  Enabled
                </Badge>
              </div>
              <div className="flex gap-2">
                <Input
                  value={webhookUrl}
                  readOnly
                  className="font-mono text-xs bg-muted flex-1"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopyWebhookUrl}
                  title="Copy URL"
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTestWebhook}
                  disabled={isTestingWebhook}
                >
                  {isTestingWebhook ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <Webhook className="h-4 w-4 mr-2" />
                      Test Webhook
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Send a POST request to this URL to trigger the workflow externally
              </p>
            </div>
          )}

          {/* Runs Table */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Workflow Runs</h2>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="show-completed"
                    checked={showCompleted}
                    onCheckedChange={setShowCompleted}
                  />
                  <Label htmlFor="show-completed" className="text-sm text-muted-foreground cursor-pointer">
                    Show completed ({completedRuns})
                  </Label>
                </div>
              </div>
            </div>

            {filteredRuns.length === 0 && workflowRuns.length === 0 ? (
              <div className="border rounded-lg p-12 text-center">
                <GitBranch className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-2">No runs yet</h3>
                <p className="text-muted-foreground mb-4">
                  Start your first workflow run to begin tracking progress.
                </p>
                <Button onClick={openRunDialog}>
                  <Play className="w-4 h-4 mr-2" />
                  Run Workflow
                </Button>
              </div>
            ) : filteredRuns.length === 0 ? (
              <div className="border rounded-lg p-8 text-center">
                <CheckCircle2 className="w-10 h-10 mx-auto text-green-500 mb-3" />
                <h3 className="text-lg font-medium mb-2">All runs completed</h3>
                <p className="text-muted-foreground mb-4">
                  Toggle "Show completed" above to view completed runs.
                </p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden bg-card shadow-sm">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Started By</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Activity</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRuns.map((run) => {
                      const progress = getRunProgress(run);
                      const dueDate = getRunDueDate(run);
                      const isOverdue = isDueDateOverdue(dueDate);

                      return (
                        <TableRow
                          key={run.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate(`/space/${projectId}/workflow-run/${run.id}`)}
                        >
                          <TableCell className="font-medium">
                            {run.run_name || 'Untitled Run'}
                          </TableCell>
                          <TableCell>
                            {run.trigger_type === 'webhook' || (!run.started_by && !run.started_by_user) ? (
                              <div className="flex items-center gap-2">
                                <div className="h-6 w-6 rounded-full bg-violet-100 flex items-center justify-center">
                                  <Webhook className="h-3.5 w-3.5 text-violet-600" />
                                </div>
                                <span className="text-sm">Webhook</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Avatar className="h-6 w-6">
                                  <AvatarImage src={run.started_by_user?.avatar_url} />
                                  <AvatarFallback className="text-xs">
                                    {run.started_by_user?.first_name?.charAt(0) || '?'}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="text-sm">{run.started_by_user ? `${run.started_by_user.first_name || ''} ${run.started_by_user.last_name || ''}`.trim() : 'Unknown'}</span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {run.started_at
                              ? formatDistanceToNow(new Date(run.started_at), { addSuffix: true })
                              : '-'
                            }
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {run.updated_date
                              ? formatDistanceToNow(new Date(run.updated_date), { addSuffix: true })
                              : '-'
                            }
                          </TableCell>
                          <TableCell>
                            {dueDate ? (
                              <div className={`flex items-center gap-1 text-sm ${isOverdue ? 'text-red-600' : 'text-muted-foreground'}`}>
                                <Calendar className="w-3.5 h-3.5" />
                                <span>{format(dueDate, 'MMM d, yyyy h:mm a')}</span>
                                {isOverdue && <span className="text-xs font-medium">(Overdue)</span>}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(run.status)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 min-w-[120px]">
                              <Progress value={progress.percent} className="h-2 flex-1" />
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {progress.completed}/{progress.total}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/space/${projectId}/workflow-run/${run.id}`);
                                }}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  View Run
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteRunId(run.id);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete Run
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>

        {/* Run Workflow Dialog */}
        <Dialog open={showRunDialog} onOpenChange={setShowRunDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Run Workflow</DialogTitle>
              <DialogDescription>
                Create a new run of "{workflow?.name}".
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Run Name</label>
                <Input
                  value={runName}
                  onChange={(e) => setRunName(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  You can edit the name above or keep the default.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRunDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateRun} disabled={isCreatingRun}>
                {isCreatingRun ? (
                  <>
                    <LoadingSpinner className="w-4 h-4 mr-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Start Run
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Delete Run Dialog */}
        <AlertDialog open={!!deleteRunId} onOpenChange={(open) => !open && setDeleteRunId(null)}>
          <AlertDialogContent className="p-0 overflow-hidden">
            <div className="p-6 space-y-4">
              <div>
                <AlertDialogTitle>Delete Run</AlertDialogTitle>
                <AlertDialogDescription className="mt-2">
                  Are you sure you want to delete this run? This action cannot be undone.
                </AlertDialogDescription>
              </div>
              <AlertDialogFooter className="gap-3 pt-2">
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                  onClick={async () => {
                    try {
                      await supabase
                        .from('checklists_workflow_runs')
                        .delete()
                        .eq('id', deleteRunId);
                      setWorkflowRuns(prev => prev.filter(r => r.id !== deleteRunId));
                      toast.success('Run deleted');
                    } catch (err) {
                      console.error('Error deleting run:', err);
                      toast.error('Failed to delete run');
                    }
                    setDeleteRunId(null);
                  }}
                >
                  Delete Run
                </AlertDialogAction>
              </AlertDialogFooter>
            </div>
          </AlertDialogContent>
        </AlertDialog>
      </PageTransition>
    </AppShell3>
  );
}
